import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Check, CreditCard, Download, Gift, Mail, QrCode } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHero, Reveal } from "@/components/shared";

const fmt = (n) => n.toFixed(2).replace(".", ",") + " €";

const addMonths = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

export default function CartesCadeaux() {
  const [cfg, setCfg] = useState(null);
  const [amount, setAmount] = useState(40);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_email: "",
    recipient_name: "",
    recipient_email: "",
    message: "",
    send_mode: "immediat",
    send_date: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/programme").then((r) => {
      setCfg(r.data);
      if (r.data.gc_preset_amounts?.length) setAmount(r.data.gc_preset_amounts[1] || r.data.gc_preset_amounts[0]);
    }).catch(() => setCfg({ gc_preset_amounts: [25, 40, 50, 75, 100], gc_min_amount: 20, gc_max_amount: 500, gc_validity_months: 12, gc_expiry_rule: "" }));
  }, []);

  const presets = cfg?.gc_preset_amounts || [25, 40, 50, 75, 100];
  const minA = cfg?.gc_min_amount ?? 20;
  const maxA = cfg?.gc_max_amount ?? 500;
  const validity = cfg?.gc_validity_months ?? 12;
  const finalAmount = custom ? parseFloat(custom.replace(",", ".")) || 0 : amount;
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const pay = async () => {
    if (!form.buyer_name || !form.buyer_email) {
      toast.error("Merci d'indiquer votre nom et votre email.");
      return;
    }
    if (finalAmount < minA || finalAmount > maxA) {
      toast.error(`Le montant doit être compris entre ${minA} € et ${maxA} €.`);
      return;
    }
    if (form.send_mode === "programme" && form.recipient_email && !form.send_date) {
      toast.error("Choisissez la date d'envoi programmé.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/gift-cards", {
        ...form,
        amount: finalAmount,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSending(false);
    }
  };

  return (
    <div data-testid="cartes-cadeaux-page">
      <PageHero overline="Cartes Cadeaux" title="Cartes cadeaux Palais Maasaï" testId="cadeaux-hero">
        <p>
          Offrez une expérience gourmande chez Palais Maasaï. Choisissez le montant de votre carte
          cadeau, personnalisez votre message et envoyez-la directement à la personne de votre choix.
        </p>
      </PageHero>

      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <div className="rounded-lg border border-white/10 bg-coal/60 p-8 md:p-10">
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">
                <Gift className="h-4 w-4 text-gold" strokeWidth={1.5} />
                Montant de la carte
              </h2>
              <div className="mt-5 flex flex-wrap gap-2" data-testid="gift-amounts">
                {presets.map((p) => (
                  <button
                    key={p}
                    onClick={() => {
                      setAmount(p);
                      setCustom("");
                    }}
                    data-testid={`gift-amount-${p}`}
                    className={`rounded-md px-5 py-3 text-sm font-semibold transition-colors duration-300 ${
                      !custom && amount === p
                        ? "bg-copper text-ivory"
                        : "border border-white/10 text-ivory/60 hover:border-gold hover:text-gold"
                    }`}
                  >
                    {p} €
                  </button>
                ))}
              </div>
              <input
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder={`Ou montant libre (${minA} € à ${maxA} €)`}
                className="input-dark mt-4"
                data-testid="gift-custom-amount"
              />

              <div className="mt-8 space-y-4 border-t border-white/10 pt-8">
                <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-ivory">
                  Vos coordonnées
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <input value={form.buyer_name} onChange={update("buyer_name")} placeholder="Votre nom et prénom" className="input-dark !h-11" data-testid="gift-buyer-name" />
                  <input type="email" value={form.buyer_email} onChange={update("buyer_email")} placeholder="Votre email" className="input-dark !h-11" data-testid="gift-buyer-email" />
                </div>

                <h3 className="pt-4 text-sm font-semibold uppercase tracking-[0.2em] text-ivory">
                  Bénéficiaire (optionnel)
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <input value={form.recipient_name} onChange={update("recipient_name")} placeholder="Nom et prénom du bénéficiaire" className="input-dark !h-11" data-testid="gift-recipient-name" />
                  <input type="email" value={form.recipient_email} onChange={update("recipient_email")} placeholder="Email du bénéficiaire" className="input-dark !h-11" data-testid="gift-recipient-email" />
                </div>
                <textarea value={form.message} onChange={update("message")} placeholder="Message personnel (optionnel)" rows={3} className="input-dark !h-auto py-3" data-testid="gift-message" />

                {form.recipient_email && (
                  <div className="space-y-3" data-testid="gift-send-mode">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ivory/60">Remise de la carte</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                        onClick={() => setForm((f) => ({ ...f, send_mode: "immediat" }))}
                        className={`flex items-center gap-3 rounded-md border p-4 text-left transition-colors ${form.send_mode === "immediat" ? "border-copper bg-copper/15" : "border-white/10 hover:border-copper/40"}`}
                        data-testid="gift-send-immediat"
                      >
                        <Mail className="h-5 w-5 text-gold" strokeWidth={1.5} />
                        <span>
                          <span className="block text-sm font-semibold text-ivory">Envoi immédiat</span>
                          <span className="block text-xs text-ivory/50">Email au bénéficiaire dès le paiement</span>
                        </span>
                      </button>
                      <button
                        onClick={() => setForm((f) => ({ ...f, send_mode: "programme" }))}
                        className={`flex items-center gap-3 rounded-md border p-4 text-left transition-colors ${form.send_mode === "programme" ? "border-copper bg-copper/15" : "border-white/10 hover:border-copper/40"}`}
                        data-testid="gift-send-programme"
                      >
                        <CalendarClock className="h-5 w-5 text-gold" strokeWidth={1.5} />
                        <span>
                          <span className="block text-sm font-semibold text-ivory">Envoi programmé</span>
                          <span className="block text-xs text-ivory/50">À la date de votre choix</span>
                        </span>
                      </button>
                    </div>
                    {form.send_mode === "programme" && (
                      <input
                        type="date"
                        value={form.send_date}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={update("send_date")}
                        className="input-dark !h-11 [color-scheme:dark]"
                        data-testid="gift-send-date"
                      />
                    )}
                    <p className="text-xs text-ivory/40">
                      Vous pourrez aussi télécharger la carte en PDF après le paiement pour l'imprimer
                      ou l'offrir vous-même.
                    </p>
                  </div>
                )}

                <div className="rounded-md border border-gold/25 bg-gold/5 p-4" data-testid="gift-validity-info">
                  <p className="text-xs leading-relaxed text-ivory/70">
                    <strong className="text-gold">Validité : {validity} mois</strong> — toute carte
                    achetée aujourd'hui sera utilisable jusqu'au{" "}
                    <strong className="text-ivory">{addMonths(validity)}</strong>. Le bénéficiaire
                    reçoit bien la valeur faciale ({fmt(finalAmount || 0)}) : aucuns frais ne sont
                    déduits de la carte.
                  </p>
                </div>

                <button onClick={pay} disabled={sending} className="btn-primary w-full" data-testid="gift-pay-button">
                  <CreditCard className="h-4 w-4" strokeWidth={1.5} />
                  {sending ? "Redirection…" : `Offrir une carte cadeau — ${fmt(finalAmount || 0)}`}
                </button>
                <p className="text-center text-[11px] leading-relaxed text-ivory/35" data-testid="gift-legal-note">
                  Paiement sécurisé par Stripe, sans frais cachés. {cfg?.gc_expiry_rule ||
                    "Cette carte cadeau est valable jusqu'à la date indiquée. Après cette date, la carte cadeau et son éventuel solde restant expirent automatiquement. Le solde non utilisé n'est ni remboursable ni échangeable, sous réserve des dispositions légales impératives applicables."}
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs text-ivory/40" data-testid="gift-features">
              <span className="flex items-center gap-2"><QrCode className="h-4 w-4 text-gold" strokeWidth={1.5} />QR code unique</span>
              <span className="flex items-center gap-2"><Download className="h-4 w-4 text-gold" strokeWidth={1.5} />PDF à imprimer</span>
              <span className="flex items-center gap-2"><Mail className="h-4 w-4 text-gold" strokeWidth={1.5} />Envoi immédiat ou programmé</span>
            </div>
            <p className="mt-6 text-center text-xs text-ivory/40">
              Déjà une carte ?{" "}
              <Link to="/espace" className="text-gold underline-offset-4 hover:underline" data-testid="gift-espace-link">
                Consultez votre solde dans votre espace
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export function GiftCardSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("loading");
  const [card, setCard] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/gift-cards/payment-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setCard(data);
          setState("paid");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts < 10) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="gift-success-page">
      <PageHero
        overline="Cartes Cadeaux"
        title={state === "paid" ? "Carte cadeau activée" : "Confirmation en cours"}
        testId="cadeaux-hero"
      />
      <section className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto max-w-2xl">
          <Reveal>
            <div className="rounded-lg border border-gold/30 bg-coal/60 p-12 text-center">
              {state === "loading" && (
                <p className="text-sm text-ivory/60" data-testid="gift-payment-loading">
                  Vérification du paiement…
                </p>
              )}
              {state === "paid" && (
                <>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/40">
                    <Check className="h-6 w-6 text-gold" strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-8 text-2xl font-semibold tracking-tight text-ivory">Merci !</h2>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-ivory/55">
                    Votre carte cadeau de {fmt(card?.amount || 0)} est active. Un email de
                    confirmation vient de vous être envoyé{card?.code ? "" : "."}
                  </p>
                  {card?.code && (
                    <>
                      <div className="mx-auto mt-8 max-w-xs rounded-lg border border-dashed border-gold/50 bg-ink px-6 py-5" data-testid="gift-code">
                        <p className="text-[11px] uppercase tracking-[0.25em] text-ivory/40">
                          Code de la carte
                        </p>
                        <p className="mt-2 text-2xl font-bold tracking-[0.2em] text-gold">
                          {card.code}
                        </p>
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/gift-cards/${card.code}/qr.png`}
                          alt="QR code de la carte cadeau"
                          className="mx-auto mt-4 h-28 w-28"
                          data-testid="gift-qr"
                        />
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/gift-cards/${card.code}/pdf`}
                        className="btn-primary mt-8"
                        data-testid="gift-pdf-download"
                      >
                        <Download className="h-4 w-4" strokeWidth={1.5} />
                        Télécharger la carte en PDF
                      </a>
                    </>
                  )}
                </>
              )}
              {state === "pending" && (
                <p className="text-sm text-ivory/60" data-testid="gift-payment-pending">
                  Paiement en cours de traitement. Vous recevrez le code par email.
                </p>
              )}
              {state === "error" && (
                <p className="text-sm text-red-400" data-testid="gift-payment-error">
                  Impossible de vérifier le paiement. Merci de nous contacter sur WhatsApp.
                </p>
              )}
              <Link to="/" className="btn-outline mt-10" data-testid="gift-success-home">
                Retour à l'accueil
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
